/** @jsx isolateComponent */

import { css } from '@emotion/core';
import { IconButton } from '../../navigation/IconButton';
import { Typography, Colors } from '../../../theme';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';
import { clamp } from '../../../utils/clamp';
import { NumberButton } from '../NumberButton';

type ButtonTypes = 'first' | 'last' | 'previous' | 'next' | 'number';

export type OnPaginateData = {
  pageIndex: number;
  pageUrl: string | undefined;
  buttonType: ButtonTypes;
};

type Props = {
  /**
   * Accessible label for the nav element.
   */
  ariaLabel?: string;

  /**
   * Current page the user is on.
   */
  currentPageIndex: number;

  /**
   * Array of page URLs. Passing an array with undefined values will skip adding links to buttons. Useful alongside PaginatorStateful.
   */
  pageUrls: Array<string | undefined>;

  /**
   * Optional handler for when a pagination button is clicked.
   */
  onPaginate?: (b: OnPaginateData, a: React.MouseEvent) => void;

  /**
   * The threshold of number of pages at which the paginator buttons will be rendered in condensed mode.
   */
  condenseAt?: number;

  /**
   * Center paginator horizontally
   */
  isCentered?: boolean;

  /**
   * Offset for page numbers displayed
   */
  offset?: number;

  /**
   * Custom URL for first page button. This will also cause the first page button to render
   */
  firstPageUrl?: string;

  /**
   * Custom URL for last page button. This will also cause the last page button to render
   */
  lastPageUrl?: string;
};

type DerivedState = {
  isCondensed: boolean;
  numberOfPages: number;
  numberOfButtons: number;
};

const Styles = labelStyles('Paginator', {
  root: (derivedState: DerivedState, props: Props) => [
    {
      display: 'grid',
      gap: 10,
      // Add two to account for the previous and next buttons.
      gridTemplateColumns: `repeat(${derivedState.numberOfButtons}, 40px)`,
      alignItems: 'center',
    },
    derivedState.isCondensed &&
      withModifier('condensed', {
        gridTemplateColumns: '44px 44px min-content 44px 44px',
      }),
    props.isCentered &&
      withModifier('isCentered', {
        justifyContent: 'center',
      }),
  ],
  text: [
    css({
      ...Typography.base,
      fontSize: 16,
      lineHeight: '22px',
      fontWeight: 600,
      textAlign: 'center',
      whiteSpace: 'nowrap',
      color: Colors.neutral800,
    }),

    css`
      padding-left: 20px;
      padding-right: 20px;
    `,
  ],
});

const PaginatorText = (props: {
  currentPageIndex: number;
  numberOfPages: number;
}) => {
  // This prevents the buttons from jostling around as the number of
  // digits in the page count increases. For example, if `numberOfPages`
  // is `150`, then there are 3 digits in that number. Double that since
  // the highest page would be `150`, reading `150 of 150`. Then add a
  // few extra characters to account for ` of `; two for `of` itself and
  // one more for the spaces around `of`, which aren't quite two characters
  // themselves.
  const maxChars = `${props.numberOfPages}`.length * 2 + 3;
  return (
    <span
      css={Styles.text}
      style={{
        minWidth: `${maxChars}ch`,
      }}
    >
      {/* Single string reads better on screen reader than multiple lines of JSX */}
      {`${props.currentPageIndex + 1} of ${props.numberOfPages}`}
    </span>
  );
};

const defaultProps = {
  condenseAt: 6,
  offset: 0,
};

/**
 * Displays a numeric list of pages that a user can choose from, with previous and next buttons.
 */
export const Paginator = (_props: Props) => {
  const props = {
    ...defaultProps,
    ..._props,
  };
  const {
    currentPageIndex,
    pageUrls,
    condenseAt,
    offset,
    firstPageUrl,
    lastPageUrl,
  } = props;
  const numberOfPages = pageUrls.length;
  const maxPageIndex = numberOfPages - 1;
  const safeCurrentPageIndex = clamp(currentPageIndex, 0, maxPageIndex);
  const prevIndex = Math.max(0, safeCurrentPageIndex - 1);
  const nextIndex = Math.min(maxPageIndex, safeCurrentPageIndex + 1);
  const isCondensed = numberOfPages >= condenseAt;
  const derivedState = {
    numberOfPages,
    isCondensed,
    numberOfButtons:
      numberOfPages + 2 + [firstPageUrl, lastPageUrl].filter(Boolean).length,
  };

  const createOnPaginateFn = (
    buttonType: ButtonTypes,
    pageIndex: number,
    pageUrl: string | undefined
  ) => {
    return (event: React.MouseEvent<Element, MouseEvent>) => {
      if (props.onPaginate) {
        props.onPaginate(
          {
            buttonType,
            pageIndex,
            pageUrl,
          },
          event
        );
      }
    };
  };

  const content = isCondensed ? (
    <PaginatorText
      currentPageIndex={safeCurrentPageIndex}
      numberOfPages={numberOfPages}
    />
  ) : (
    pageUrls.map((pageUrl, pageIndex) => {
      const isSelected = pageIndex === safeCurrentPageIndex;
      const pageNumber = offset + pageIndex + 1;
      return (
        <NumberButton
          ariaLabel={
            isSelected ? `Current page (${pageNumber})` : `${pageNumber}`
          }
          href={pageUrl}
          key={pageUrl || pageNumber}
          onClick={createOnPaginateFn('number', pageIndex, pageUrl)}
          value={pageNumber}
          selected={isSelected}
          hasShadow={isSelected}
        />
      );
    })
  );

  const firstPageButtonUrl =
    firstPageUrl || (isCondensed ? pageUrls[0] : undefined);
  const firstPageButton = (firstPageButtonUrl || isCondensed) && (
    <IconButton
      ariaLabel="First page"
      color="icon-secondary"
      disabled={currentPageIndex <= 0}
      href={firstPageButtonUrl}
      icon="arrowLineLeft"
      onClick={createOnPaginateFn('first', 0, firstPageButtonUrl)}
      tooltipPosition="southwest"
    />
  );

  const lastPageButtonUrl =
    lastPageUrl || (isCondensed ? pageUrls[maxPageIndex] : undefined);
  const lastPageButton = (lastPageButtonUrl || isCondensed) && (
    <IconButton
      ariaLabel="Last page"
      color="icon-secondary"
      disabled={safeCurrentPageIndex >= maxPageIndex}
      href={lastPageButtonUrl}
      icon="arrowLineRight"
      onClick={createOnPaginateFn('last', maxPageIndex, lastPageButtonUrl)}
      tooltipPosition="southeast"
    />
  );

  return (
    <nav aria-label={props.ariaLabel} css={[Styles.root(derivedState, props)]}>
      {firstPageButton}
      <IconButton
        ariaLabel={`Previous page (${prevIndex + 1})`}
        color="icon-secondary"
        disabled={safeCurrentPageIndex <= 0}
        href={pageUrls[prevIndex]}
        icon="arrowSmallLeft"
        onClick={createOnPaginateFn('previous', prevIndex, pageUrls[prevIndex])}
        tooltipPosition="southwest"
      />
      {content}
      <IconButton
        ariaLabel={`Next page (${nextIndex + 1})`}
        color="icon-secondary"
        disabled={safeCurrentPageIndex >= maxPageIndex}
        href={pageUrls[nextIndex]}
        icon="arrowSmallRight"
        onClick={createOnPaginateFn('next', nextIndex, pageUrls[nextIndex])}
        tooltipPosition="southeast"
      />
      {lastPageButton}
    </nav>
  );
};
